import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Hamburger } from '@withjoy/joykit/icons';
import type { UniversalBurgerMenuEmbedHandlers } from '@apps/admin/components/UniversalBurgerMenu/UniversalBurgerMenu.types';
import { UniversalBurgerMenu } from '@apps/admin/components/UniversalBurgerMenu/UniversalBurgerMenu';
import type { ParticleJoyLogoHandle, ParticleJoyLogoMark } from '@shared/components/ParticleJoyLogo';
import { StyledHamburgerWrapper, StyledMenuTriggerZone, StyledParticleJoyLogo } from './BurgerLogoTrigger.styles';

type BurgerLogoTriggerProps = Readonly<{
  /** Unique DOM id for the particle logo — keep distinct per mount point so the particle effect
   *  initialises against the right canvas. */
  logoId: string;
  /** Forwarded to `UniversalBurgerMenu` so the drawer opens against the right event. */
  eventHandle?: string;
  hamburgerSize?: number;
  hideHamburger?: boolean;
  hideLogo?: boolean;
  /** `true` keeps the logo visible at all breakpoints (unified nav header); `false` (default)
   *  hides it below `md` — the admin header's behaviour, where the hamburger already carries Joy
   *  branding on mobile. */
  logoAlwaysVisible?: boolean;
  /** Rendered logo dimensions (the SVG keeps its 54×34 aspect ratio). */
  logoWidth?: number;
  logoHeight?: number;
  /** Vertical nudge to optically centre the logo against the hamburger. */
  logoTranslateY?: number;
  logoColor?: string;
  /** Cut of the Joy mark. `xs` is the brand's small-size cut; it fills a 27×17 box, so pass
   *  `logoWidth` / `logoHeight` at that ratio. @see ParticleJoyLogo */
  logoMark?: ParticleJoyLogoMark;
  /** Theme space-scale index for the hamburger↔logo gap. Defaults to 4 (the admin header's original
   *  spacing); the unified-shell nav passes 3 per Figma. */
  columnGap?: number;
  /** Notified whenever the drawer opens or closes. Only the iframe embed needs this — it has to
   *  tell its host to grow the frame to full-viewport before the drawer can paint. */
  onOpenChange?: (isOpen: boolean) => void;
  /** Host overrides for actions that can't complete inside an iframe. @see UniversalBurgerMenu */
  embed?: UniversalBurgerMenuEmbedHandlers;
}>;

/**
 * Bridges `BurgerMenu`'s render-prop `isOpen` out to `onOpenChange`. This is a component rather
 * than a `useEffect` in the render prop itself: the render prop runs inside `BurgerMenu`'s render,
 * so a hook there would join *its* hook order conditionally.
 */
const OpenStateBridge: React.FC<Readonly<{ isOpen: boolean; onOpenChange?: (isOpen: boolean) => void }>> = ({ isOpen, onOpenChange }) => {
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);
  return null;
};

/**
 * The hamburger + particle Joy-logo trigger cluster, shared by `AdminHeaderBar` and the unified
 * website editor's `ShellNavBar`. `UniversalBurgerMenu` owns the open/close + drawer; the children
 * are purely the visual trigger. Hovering anywhere in the zone highlights the hamburger and bursts
 * the logo particles.
 */
export const BurgerLogoTrigger: React.FC<BurgerLogoTriggerProps> = ({
  logoId,
  eventHandle,
  hamburgerSize = 32,
  hideHamburger = false,
  hideLogo = false,
  logoAlwaysVisible = false,
  logoWidth = 50.824,
  logoHeight = 32,
  logoTranslateY,
  logoColor = '#1D1D1F',
  logoMark,
  columnGap = 4,
  onOpenChange,
  embed,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const particleLogoRef = useRef<ParticleJoyLogoHandle>(null);

  const handleTriggerZoneMouseMove = useCallback(() => {
    if (!hideLogo && particleLogoRef.current) {
      particleLogoRef.current.triggerParticles();
    }
  }, [hideLogo]);

  return (
    <UniversalBurgerMenu
      eventHandle={eventHandle}
      embed={embed}
    >
      {({ isOpen }: { isOpen: boolean }) => (
        <StyledMenuTriggerZone
          $columnGap={columnGap}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onMouseMove={handleTriggerZoneMouseMove}
          data-testid="menu-trigger-zone"
        >
          <OpenStateBridge
            isOpen={isOpen}
            onOpenChange={onOpenChange}
          />
          {!hideHamburger && (
            <StyledHamburgerWrapper
              $isHovered={isHovered}
              $isActive={isOpen}
            >
              <Hamburger size={hamburgerSize} />
            </StyledHamburgerWrapper>
          )}
          {!hideLogo && (
            <StyledParticleJoyLogo
              ref={particleLogoRef}
              id={logoId}
              logoColor={logoColor}
              mark={logoMark}
              $width={logoWidth}
              $height={logoHeight}
              $alwaysVisible={logoAlwaysVisible}
              $translateY={logoTranslateY}
            />
          )}
        </StyledMenuTriggerZone>
      )}
    </UniversalBurgerMenu>
  );
};

BurgerLogoTrigger.displayName = 'BurgerLogoTrigger';
